import { useEffect, useState } from 'react';
import { useTheme } from '../../../stores/useTheme';
import { MinimizedStatsConsole } from './MinimizedStatsConsole';
import { StandardAndExpandedStatsConsole } from './StandardAndExpandedStatsConsole/StandardAndExpandedStatsConsole';
import { getH2HData, getLineupData, getOverviewDataFromOurBackend, getPlayersTabData, getStatsConsoleData, getStatsTabData, getTeamNames } from '@ihs-widget/api/gettersMartech';
import { getFixturesH2HData, getFixturesLineupData, getFixturesPlayersTabData, getFixturesStatsConsoleData, getFixturesStatsTabData, getOverviewFixturesDataFromOurBackend, getFixturesTeamNames } from '@ihs-widget/api/gettersMockData';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getUrlParam } from '@ihs-widget/utils/functions';
import { useValidatedApiData } from '@ihs-widget/utils/hooks/useValidatedApiData';
import {
    isValidH2HResponse,
    isValidLineupResponse,
    isValidOverviewResponse,
    isValidPlayersTabResponse,
    isValidStatsConsoleResponse,
    isValidStatsTabResponse,
    isValidTeamNamesResponse,
} from '@ihs-widget/utils/validateApiResponse';
import { T, UrlParams } from '@ihs-widget/utils/enums';
import { useDataOrchestrator } from '@ihs-widget/stores/DataOrchestrator';
import { useMatchTime } from '@ihs-widget/utils/hooks/useMatchTime';

export type WidgetState = 'minimized' | 'standard' | "expanded";

interface ILineup {
    state: WidgetState;
    isMVP?: boolean;
}

const StatsConsole = ({ state = 'minimized', isMVP = true }: ILineup) => {
    const [currentStatIndex, setCurrentStatIndex] = useState(0);
    const eventId = getUrlParam(UrlParams.EVENT_ID);
    const themeURL = getUrlParam(UrlParams.THEME);
    const isFixtures = getUrlParam(UrlParams.FIXTURES) === 'true';
    const fixedHeight = getUrlParam(UrlParams.FIXED_HEIGHT) ?? '341px';

    const { theme, toggleTheme } = useTheme();

    const { ready } = useDataOrchestrator();
    
    const overviewData = useQuery({
        queryKey: ['stats_console_overview_data_by_id', eventId, isFixtures],
        queryFn: () => isFixtures 
            ? getOverviewFixturesDataFromOurBackend(eventId) 
            : getOverviewDataFromOurBackend(eventId),
        refetchInterval: 5000,
        placeholderData: keepPreviousData,
        enabled: ready.overview && (state === 'expanded')
    });
    const validatedOverviewData = useValidatedApiData<any>(overviewData.data, isValidOverviewResponse);
    
    const matchTime = useMatchTime({
        matchClockData: validatedOverviewData?.data?.match_clock || null,
        isEnabled: true
    });

    const statsConsoleData = useQuery({
        queryKey: ['stats_console_main_data', eventId, isFixtures],
        queryFn: () => isFixtures 
            ? getFixturesStatsConsoleData(eventId)
            : getStatsConsoleData(eventId),
        refetchInterval: 5000,
        placeholderData: keepPreviousData,
        enabled: ready.minimized && (state === 'minimized' || state === 'standard')
    });
    const validatedStatsConsoleData = useValidatedApiData<any[]>(statsConsoleData.data, isValidStatsConsoleResponse);

    const statsTabData = useQuery({
        queryKey: ['stats_console_stats_data', eventId, isFixtures],
        queryFn: () => isFixtures 
            ? getFixturesStatsTabData(eventId)
            : getStatsTabData(eventId),
        refetchInterval: 5000,
        placeholderData: keepPreviousData,
        enabled: ready.stats && (state === 'expanded')
    });
    const validatedStatsTabData = useValidatedApiData<any>(statsTabData.data, isValidStatsTabResponse);

    const playersTabData = useQuery({
        queryKey: ['stats_console_players_data', eventId, isFixtures],
        queryFn: () => isFixtures 
            ? getFixturesPlayersTabData(eventId)
            : getPlayersTabData(eventId),
        refetchInterval: 5000,
        placeholderData: keepPreviousData,
        enabled: ready.players && (state === 'expanded')
    });
    const validatedPlayersTabData = useValidatedApiData<any>(playersTabData.data, isValidPlayersTabResponse);

    const h2hData = useQuery({
        queryKey: ['stats_console_h2h_data', eventId, isFixtures],
        queryFn: () => isFixtures 
            ? getFixturesH2HData(eventId)
            : getH2HData(eventId),
        staleTime: Infinity,
        placeholderData: keepPreviousData,
        enabled: ready.h2h && (state === 'expanded')
    });
    const validatedH2hData = useValidatedApiData<any>(h2hData.data, isValidH2HResponse);

    const lineupData = useQuery({
        queryKey: ['stats_console_lineup_data', eventId, isFixtures],
        queryFn: () => isFixtures 
            ? getFixturesLineupData(eventId)
            : getLineupData(eventId),
        refetchInterval: 5000,
        placeholderData: keepPreviousData,
        enabled: ready.lineup && (state === 'expanded')
    });
    const validatedLineupData = useValidatedApiData<any>(lineupData.data, isValidLineupResponse);

    const teamNamesData = useQuery({
        queryKey: ['stats_console_team_names', eventId, isFixtures],
        queryFn: () => isFixtures 
            ? getFixturesTeamNames(eventId)
            : getTeamNames(eventId),
        staleTime: Infinity,
        placeholderData: keepPreviousData,
        enabled: ready.teamNames && (state === 'expanded')
    });
    const validatedTeamNamesData = useValidatedApiData<any>(teamNamesData.data, isValidTeamNamesResponse);

    useEffect(() => {
        if (themeURL) {
            toggleTheme(themeURL);
        }
    }, [themeURL])


    useEffect(() => {
        if (validatedStatsConsoleData?.data?.length) {
            const interval = setInterval(() => {
                setCurrentStatIndex((prevIndex) => 
                    (prevIndex + 1) % validatedStatsConsoleData.data!.length
                );
            }, 10000);

            return () => clearInterval(interval);
        }
    }, [validatedStatsConsoleData]);



    const renderComponenet = () => {

        switch (state.toLocaleLowerCase()) {
            case 'minimized':
                return (
                    <MinimizedStatsConsole 
                        homeScore={validatedStatsConsoleData?.data?.[currentStatIndex]?.home_score ?? null} 
                        awayScore={validatedStatsConsoleData?.data?.[currentStatIndex]?.away_score ?? null} 
                        title={validatedStatsConsoleData?.data?.[currentStatIndex]?.title ?? null}
                    />
                )

            case 'standard':
                return (
                    <StandardAndExpandedStatsConsole 
                        isExpanded={false} 
                        eventId={eventId} 
                        overviewData={validatedOverviewData} 
                        statsTabData={validatedStatsTabData?.data?.match_statistics}
                        playersTabData={validatedPlayersTabData}
                        h2hData={validatedH2hData}
                        lineupData={validatedLineupData}
                        homeScore={validatedStatsConsoleData?.data?.[currentStatIndex]?.home_score ?? null} 
                        awayScore={validatedStatsConsoleData?.data?.[currentStatIndex]?.away_score ?? null}
                        title={validatedStatsConsoleData?.data?.[currentStatIndex]?.title ?? null}
                        isMVP={isMVP}
                        matchTime={matchTime}
                        homeTeamName={validatedTeamNamesData?.data?.home_team ?? null}
                        awayTeamName={validatedTeamNamesData?.data?.away_team ?? null}
                    />
                )
            case 'expanded':
                return (
                    <StandardAndExpandedStatsConsole 
                        isExpanded={true} 
                        eventId={eventId} 
                        overviewData={validatedOverviewData} 
                        homeScore={validatedStatsConsoleData?.data?.[currentStatIndex]?.home_score ?? null} 
                        awayScore={validatedStatsConsoleData?.data?.[currentStatIndex]?.away_score ?? null} 
                        statsTabData={validatedStatsTabData?.data?.match_statistics}
                        playersTabData={validatedPlayersTabData}
                        h2hData={validatedH2hData}
                        lineupData={validatedLineupData}
                        title={validatedStatsConsoleData?.data?.[currentStatIndex]?.title ?? null}
                        isMVP={isMVP}
                        fixedHeight={fixedHeight}
                        matchTime={matchTime}
                        homeTeamName={validatedTeamNamesData?.data?.home_team ?? null}
                        awayTeamName={validatedTeamNamesData?.data?.away_team ?? null}
                    />
                )
            default:
                return (
                    <MinimizedStatsConsole 
                        homeScore={validatedStatsConsoleData?.data?.[currentStatIndex]?.home_score ?? null} 
                        awayScore={validatedStatsConsoleData?.data?.[currentStatIndex]?.away_score ?? null} 
                        title={validatedStatsConsoleData?.data?.[currentStatIndex]?.title ?? null}
                    />
                )
        }
    }

    return (
        <div className={`bg-transparent ${theme === T.DARK ? T.DARK : ''}`}>
            {renderComponenet()}
        </div>
    );
};

export default StatsConsole;
